/**
 * PATH: apps/hr/src/pages/documents/documents-list/StatusBadges.tsx
 * PURPOSE: StatCard, ConfidenceBadge, and StatusBadge components for documents
 * WHY: Groups small display components used across the documents page
 */

import { Clock, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { DocumentStatus } from '../../../types'

export function StatCard({ label, value, icon: Icon, color, onClick, isActive }: {
  label: string
  value: number
  icon: React.ElementType
  color: string
  onClick?: () => void
  isActive?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 text-left w-full transition-all",
        onClick && "hover:shadow-md cursor-pointer",
        isActive && "ring-2 ring-teal-500"
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', color)}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-semibold text-slate-900 dark:text-white">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </button>
  )
}

export function ConfidenceBadge({ confidence }: { confidence: number }) {
  const color = confidence >= 90 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                confidence >= 70 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', color)}>
      {confidence}%
    </span>
  )
}

export function StatusBadge({ status }: { status: DocumentStatus }) {
  const statusStyles: Record<DocumentStatus, { bg: string; text: string; icon: React.ElementType }> = {
    pending_ocr: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', icon: Clock },
    needs_review: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: AlertTriangle },
    approved: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
    rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: XCircle },
    expired: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: XCircle },
  }

  const statusLabels: Record<DocumentStatus, string> = {
    pending_ocr: 'Processing',
    needs_review: 'Needs Review',
    approved: 'Approved',
    rejected: 'Rejected',
    expired: 'Expired',
  }

  const StatusIcon = statusStyles[status].icon

  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium',
      statusStyles[status].bg,
      statusStyles[status].text
    )}>
      <StatusIcon className="w-3 h-3" />
      {statusLabels[status]}
    </span>
  )
}
